import { append, patch, removeItem } from '@ngxs/store/operators';
import { Injectable } from '@angular/core';
import {
  Action,
  State,
  StateContext,
  StateToken,
  Store,
  createSelector
} from '@ngxs/store';
import { forkJoin, switchMap, tap } from 'rxjs';
import {
  HostModeDiner,
  PendingAssignment,
  PendingAssignmentStatusChangeType
} from 'src/app/models';
import { HostModeService } from 'src/app/services/host-mode.service';
import {
  AddToPendingDiners,
  ClosePendingSeatAssignment,
  DeletePendingAssignment,
  GetPendingAssignments,
  HostModeDeselectTable,
  HostModeSelectTable,
  NewSeatAssignments,
  NewSeatAssignmentsSuccess,
  PendingAssignmentDeletedWsEvent,
  PendingSeatCreatedWsEvent,
  PendingSeatCreatedWsEventSuccess,
  RemovePendingDiner
} from './host-mode.action';
import { DiningService } from 'src/app/services';
import {
  PendingSeatAssignmentSeatedWsEvent,
  UpdateLocalTables,
  UpdatePendingSeatAssignment
} from 'src/app/store/tables/tables.action';
import { HostModeHelper } from 'src/app/helpers';
import { TABLES_STATE_TOKEN } from 'src/app/store/tables/tables.state.model';

export interface HostModeStateModel {
  selectedTable: number;
  diners: HostModeDiner[];
  pendingSeatAssignments: PendingAssignment[];
}

export const HOST_MODE_STATE_TOKEN = new StateToken<HostModeStateModel>(
  'host_mode'
);

@State({
  name: HOST_MODE_STATE_TOKEN,
  defaults: {
    selectedTable: null,
    diners: [],
    pendingSeatAssignments: []
  }
})
@Injectable()
export class HostModeState {
  constructor(
    private readonly hostModeService: HostModeService,
    private readonly store: Store,
    private readonly diningService: DiningService
  ) {}

  @Action(HostModeSelectTable)
  selectTable(
    { patchState }: StateContext<HostModeStateModel>,
    { id: selectedTable }: HostModeSelectTable
  ) {
    patchState({ selectedTable, diners: [] });
  }

  @Action(HostModeDeselectTable)
  deselectTable({ patchState }: StateContext<HostModeStateModel>) {
    patchState({ selectedTable: null, diners: [] });
  }

  @Action(AddToPendingDiners)
  addToPendingDiners(
    { setState }: StateContext<HostModeStateModel>,
    { diner }: AddToPendingDiners
  ) {
    setState(
      patch({
        diners: append([diner])
      })
    );
  }

  @Action(NewSeatAssignments)
  newSeatAssignments(
    { getState, dispatch }: StateContext<HostModeStateModel>,
    { diners, tables }: NewSeatAssignments
  ) {
    const selectedTable = tables.find(
      (table) => table.id === getState().selectedTable
    );
    const newSeatAssignment$ = diners.map((diner) =>
      this.hostModeService.newSeatAssignment({
        ...diner,
        table_id: selectedTable.id
      })
    );
    return forkJoin(newSeatAssignment$).pipe(
      tap((response) => {
        const newDiners = diners.map(
          (diner, index) =>
            <PendingAssignment>{
              ...diner,
              id: response[index].seat_assignment_id,
              table_name: selectedTable.name,
              table_id: selectedTable.id,
              created_at: new Date()
            }
        );
        dispatch([
          new NewSeatAssignmentsSuccess(newDiners),
          new HostModeDeselectTable(false, getState().selectedTable)
        ]);
      })
    );
  }

  @Action(NewSeatAssignmentsSuccess)
  newSeatAssignmentsSuccess(
    ctx: StateContext<HostModeStateModel>,
    payload: NewSeatAssignmentsSuccess
  ) {
    ctx.setState(
      patch<HostModeStateModel>({
        pendingSeatAssignments: append(payload.pendingSeatAssignments)
      })
    );
    ctx.dispatch(
      new UpdatePendingSeatAssignment(
        PendingAssignmentStatusChangeType.CREATED,
        {
          createdItems: payload.pendingSeatAssignments,
          tableId: payload.pendingSeatAssignments[0].table_id
        }
      )
    );
  }

  @Action(RemovePendingDiner)
  removePendingDiners(
    ctx: StateContext<HostModeStateModel>,
    { index }: RemovePendingDiner
  ) {
    ctx.setState(
      patch<HostModeStateModel>({
        diners: removeItem<HostModeDiner>(index)
      })
    );
  }

  @Action(GetPendingAssignments)
  getPendingAssignments({ patchState }: StateContext<HostModeStateModel>) {
    return this.hostModeService
      .getPendingAssignments()
      .pipe(
        tap((pendingSeatAssignments) => patchState({ pendingSeatAssignments }))
      );
  }

  @Action(DeletePendingAssignment)
  deletePendingAssignment(
    ctx: StateContext<HostModeStateModel>,
    { id, fromWS }: DeletePendingAssignment
  ) {
    const pendingSeatAssignments = ctx.getState().pendingSeatAssignments;

    const idx = pendingSeatAssignments.findIndex((i) => i.id === id);

    if (idx === -1) {
      return;
    }

    if (fromWS) {
      this.deleteLocalAssignment(idx, pendingSeatAssignments, id, ctx);
      return;
    }

    return this.hostModeService.deleteAssignment(id);
  }

  @Action(PendingAssignmentDeletedWsEvent)
  pendingAssignmentDeletedWsEvent(
    ctx: StateContext<HostModeStateModel>,
    payload: PendingAssignmentDeletedWsEvent
  ) {
    const pendingSeatAssignments = ctx.getState().pendingSeatAssignments;

    const idx = pendingSeatAssignments.findIndex((i) => i.id === payload.id);

    if (idx === -1) {
      return;
    }

    this.deleteLocalAssignment(idx, pendingSeatAssignments, payload.id, ctx);
  }

  @Action(ClosePendingSeatAssignment)
  closePendingSeatAssignment(
    ctx: StateContext<HostModeStateModel>,
    { id, dinerId }: ClosePendingSeatAssignment
  ) {
    const pendingSeatAssignments = ctx.getState().pendingSeatAssignments;
    const idx = pendingSeatAssignments.findIndex((i) => i.id === id);
    if (idx === -1) {
      return;
    }
    this.deleteLocalAssignment(idx, pendingSeatAssignments, id, ctx);
    return this.hostModeService.closeAssignment(id);
  }

  @Action(PendingSeatAssignmentSeatedWsEvent)
  pendingSeatAssignmentSeatedWsEvent(
    ctx: StateContext<HostModeStateModel>,
    payload: PendingSeatAssignmentSeatedWsEvent
  ) {
    const id = payload.id;
    const pendingSeatAssignments = ctx.getState().pendingSeatAssignments;
    const idx = pendingSeatAssignments.findIndex((i) => i.id === id);
    if (idx === -1) {
      return;
    }
    this.deleteLocalAssignment(idx, pendingSeatAssignments, id, ctx);
  }

  @Action(PendingSeatCreatedWsEvent)
  pendingSeatCreatedWsEvent(
    { dispatch }: StateContext<HostModeStateModel>,
    { tableId }: PendingSeatCreatedWsEvent
  ) {
    return this.diningService
      .getTableDetail(tableId)
      .pipe(
        switchMap((table) =>
          dispatch(new PendingSeatCreatedWsEventSuccess(table))
        )
      );
  }

  @Action(PendingSeatCreatedWsEventSuccess)
  pendingSeatCreatedWsEventSuccess(
    { setState, dispatch }: StateContext<HostModeStateModel>,
    payload: PendingSeatCreatedWsEventSuccess
  ) {
    const { table } = payload;
    const tableId = table.id;
    const tablesById =
      this.store.selectSnapshot(TABLES_STATE_TOKEN).tables.byId;
    const normalizedTableById = tablesById[tableId];
    const pendingSeatAssignmentsByTableStored =
      normalizedTableById.pending_seat_assignments;
    const pendingSeatAssignment = table.pending_seat_assignments;

    let isReturnTable = false;

    if (
      pendingSeatAssignmentsByTableStored &&
      (pendingSeatAssignmentsByTableStored.length === 0 ||
        pendingSeatAssignmentsByTableStored.length !==
          pendingSeatAssignment.length)
    ) {
      const pendingSeatAssignments =
        HostModeHelper.getPendingAssignmentFromFloorPlan(
          pendingSeatAssignment,
          table.id
        ).filter((item) =>
          pendingSeatAssignmentsByTableStored.every((i) => i.id !== item.id)
        );
      if (pendingSeatAssignments.length) {
        isReturnTable = true;
        setState(
          patch<HostModeStateModel>({
            pendingSeatAssignments: append<PendingAssignment>(
              pendingSeatAssignments
            )
          })
        );
      }
    }

    if (isReturnTable) {
      return dispatch(new UpdateLocalTables([table]));
    }
  }

  private deleteLocalAssignment(
    idx: number,
    pendingSeatAssignments: PendingAssignment[],
    deletedId: number,
    ctx: StateContext<HostModeStateModel>
  ) {
    const tableId = pendingSeatAssignments[idx].table_id;
    ctx.setState(
      patch<HostModeStateModel>({
        pendingSeatAssignments: removeItem(idx)
      })
    );
    ctx.dispatch(
      new UpdatePendingSeatAssignment(
        PendingAssignmentStatusChangeType.DELETED,
        {
          deletedId,
          tableId
        }
      )
    );
  }
}
